import React, { createContext, useContext, useState, useEffect } from 'react';
import { setSoundSettings } from '../hooks/soundEffectsManager';
import { useScreenLockAudio } from '../hooks/useScreenLockAudio';

const MusicContext = createContext();

export function MusicProvider({ children }) {
  // Handle screen lock/unlock for audio pause/resume
  useScreenLockAudio();

  // Music settings
  const [musicEnabled, setMusicEnabled] = useState(() => {
    const saved = localStorage.getItem('musicEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  const [musicVolume, setMusicVolume] = useState(() => {
    const saved = localStorage.getItem('musicVolume');
    return saved !== null ? parseFloat(saved) : 0.5;
  });

  // Sound effects settings
  const [soundsEnabled, setSoundsEnabled] = useState(() => {
    const saved = localStorage.getItem('soundsEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  const [soundsVolume, setSoundsVolume] = useState(() => {
    const saved = localStorage.getItem('soundsVolume');
    return saved !== null ? parseFloat(saved) : 0.5;
  });

  const [masterVolume, setMasterVolume] = useState(() => {
    const saved = localStorage.getItem('masterVolume');
    return saved !== null ? parseFloat(saved) : 1;
  });

  // Save to localStorage and sync with global manager whenever settings change
  useEffect(() => {
    localStorage.setItem('musicEnabled', musicEnabled.toString());
  }, [musicEnabled]);

  useEffect(() => {
    localStorage.setItem('musicVolume', musicVolume.toString());
  }, [musicVolume]);

  useEffect(() => {
    localStorage.setItem('soundsEnabled', soundsEnabled.toString());
  }, [soundsEnabled]);

  useEffect(() => {
    localStorage.setItem('soundsVolume', soundsVolume.toString());
  }, [soundsVolume]);

  useEffect(() => {
    localStorage.setItem('masterVolume', masterVolume.toString());
  }, [masterVolume]);

  useEffect(() => {
    setSoundSettings(soundsEnabled, soundsVolume, masterVolume);
  }, [soundsEnabled, soundsVolume, masterVolume]);

  const toggleMusic = () => {
    setMusicEnabled(prev => !prev);
  };

  const toggleSounds = () => {
    setSoundsEnabled(prev => !prev);
  };

  const toggleAll = () => {
    // If both are enabled, disable both. Otherwise, enable both.
    const newState = !(musicEnabled && soundsEnabled);
    setMusicEnabled(newState);
    setSoundsEnabled(newState);
  };

  return (
    <MusicContext.Provider value={{ 
      musicEnabled, 
      musicVolume,
      soundsEnabled,
      soundsVolume,
      masterVolume,
      toggleMusic, 
      toggleSounds,
      toggleAll,
      setMusicVolume,
      setSoundsVolume,
      setMasterVolume
    }}>
      {children}
    </MusicContext.Provider>
  );
}

export function useMusic() {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error('useMusic must be used within a MusicProvider');
  }
  return context;
}

